/**
 * MassContractUploadModal — Multi-step modal for bulk contract upload & analysis (KAN-238)
 *
 * Steps:
 *   1. scope    — Select counterparty + value date, see eligible trades
 *   2. upload   — Select PDF files, upload to temp GCS, run BTN matching
 *   3. matching — Show auto-match results, manual-match unmatched PDFs
 *   4. review   — Review all pairs, choose "Upload" or "Upload & Analyse"
 *   5. processing — Progress overlay while processing
 *   6. results  — Final results table
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, Upload, FileText, CheckCircle, XCircle, ChevronLeft, ChevronRight, Loader2, Trash2, Undo2, Paperclip } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { bankService } from '../../services/api/bankService';
import type { Bank } from '../../services/api/bankService';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './MassContractUploadModal.css';

// Re-use the same worker as ContractComparison
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ModalStep = 'scope' | 'upload' | 'matching' | 'review' | 'processing' | 'results';

interface MassContractUploadModalProps {
  isOpen: boolean;
  clientId: string;
  onClose: () => void;
  onComplete: () => void;  // refresh the trades grid after processing
}

interface EligibleTrade {
  id: string;
  entityId?: string;
  TradeNumber: string;
  bankTradeNumber: string | null;
  OurParty?: string;
  ValueDate: string;
  QuantityCurrency1: number;
  Currency1: string;
  Currency2: string;
  Price: number;
  MaturityDate: string;
  CounterpartyId: string;
  CounterpartyName: string;
  Direction: string;
  ProductType: string;
}

interface UploadedFile {
  filename: string;
  temp_path: string;
  signed_url: string;
}

interface MatchedPair {
  filename: string;
  temp_path: string;
  trade_id: string;
  trade_number: string;
  btn: string;
  entity_id?: string;
  signed_url?: string;
  matchType: 'auto' | 'manual';
}

interface UnmatchedFile {
  filename: string;
  temp_path: string;
  signed_url?: string;
}

interface ProcessResult {
  trade_id: string;
  trade_number?: string;
  status: 'ok' | 'error';
  contract_status?: string;
  error?: string;
}

const MassContractUploadModal: React.FC<MassContractUploadModalProps> = ({
  isOpen,
  clientId,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();

  // Step state
  const [step, setStep] = useState<ModalStep>('scope');

  // Step 1: Scope
  const [banks, setBanks] = useState<Bank[]>([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [selectedCounterpartyId, setSelectedCounterpartyId] = useState('');
  const [selectedValueDate, setSelectedValueDate] = useState('');
  const [eligibleTrades, setEligibleTrades] = useState<EligibleTrade[]>([]);
  const [selectedTradeIds, setSelectedTradeIds] = useState<Set<string>>(new Set());
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Step 2: Upload
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [uploading, setUploading] = useState(false);

  // Step 3: Matching
  const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
  const [unmatchedFiles, setUnmatchedFiles] = useState<UnmatchedFile[]>([]);
  const [unmatchedTradeIds, setUnmatchedTradeIds] = useState<string[]>([]);
  const [manualMatchIndex, setManualMatchIndex] = useState(0);
  const [skippedFiles, setSkippedFiles] = useState<UnmatchedFile[]>([]);
  const [pdfNumPages, setPdfNumPages] = useState(1);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState(500);
  const [singleAssignTradeId, setSingleAssignTradeId] = useState<string | null>(null);

  // Size PDF page to fit entirely within the visible panel (no vertical scroll)
  useEffect(() => {
    if (step !== 'matching' || !pdfContainerRef.current) return;
    const measure = () => {
      if (pdfContainerRef.current) {
        const cw = pdfContainerRef.current.clientWidth;
        setPdfWidth(cw * 0.60);
      }
    };
    requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, [step]);

  // Step 5: Processing
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0 });

  // Step 6: Results
  const [processResults, setProcessResults] = useState<ProcessResult[]>([]);

  // Error
  const [error, setError] = useState('');

  // ─── Load banks on first open ───
  const loadBanks = useCallback(async () => {
    if (banksLoaded) return;
    try {
      const response = await bankService.listBanks();
      if (response.success && response.data) {
        setBanks(response.data.filter((b: Bank) => b.status === 'active' && b.isCounterparty !== false));
      }
      setBanksLoaded(true);
    } catch (e) {
      console.error('Failed to load banks:', e);
    }
  }, [banksLoaded]);

  // Load banks when modal opens
  React.useEffect(() => {
    if (isOpen) {
      loadBanks();
    }
  }, [isOpen, loadBanks]);

  // ─── Step 1: Fetch eligible trades ───
  const fetchEligibleTrades = useCallback(async () => {
    if (!selectedCounterpartyId || !selectedValueDate) return;
    setLoadingTrades(true);
    setError('');
    setHasSearched(false);
    try {
      const trades = await clientService.getMassUploadEligibleTrades(
        clientId,
        selectedCounterpartyId,
        selectedValueDate,
      );
      setEligibleTrades(trades);
      setSelectedTradeIds(new Set(trades.map((t: EligibleTrade) => t.id)));
    } catch (e: any) {
      setError(e.message || 'Failed to fetch eligible trades');
    } finally {
      setLoadingTrades(false);
      setHasSearched(true);
    }
  }, [clientId, selectedCounterpartyId, selectedValueDate]);

  // ─── Step 2: Upload files ───
  const handleFileUpload = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);

    // Validate count
    if (files.length > selectedTradeIds.size) {
      setError(t('massUpload.tooManyFiles', {
        files: files.length,
        trades: selectedTradeIds.size,
      }));
      return;
    }

    setUploading(true);
    setError('');
    try {
      // Build trade-to-entity map for child entity trades
      const tradeEntityMap: Record<string, string> = {};
      for (const trade of eligibleTrades) {
        if (trade.entityId && selectedTradeIds.has(trade.id)) {
          tradeEntityMap[trade.id] = trade.entityId;
        }
      }

      const result = await clientService.massUploadContracts(
        clientId,
        files,
        Array.from(selectedTradeIds),
        tradeEntityMap,
      );

      setSessionId(result.session_id);
      setUploadedFiles(result.files);

      // Process matching results
      const matching = result.matching;
      const autoMatched: MatchedPair[] = (matching.matched || []).map((m: any) => ({
        ...m,
        matchType: 'auto' as const,
      }));
      setMatchedPairs(autoMatched);
      setUnmatchedFiles(matching.unmatched_files || []);
      setUnmatchedTradeIds(matching.unmatched_trade_ids || []);

      // If all matched, skip to review; otherwise go to matching step
      if ((matching.unmatched_files || []).length === 0) {
        setStep('review');
      } else {
        setManualMatchIndex(0);
        setPdfPageNumber(1);
        setStep('matching');
      }
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [clientId, selectedTradeIds, t]);

  // ─── Step 3: Manual matching ───
  const currentUnmatchedFile = unmatchedFiles[manualMatchIndex] || null;

  const remainingUnmatchedTrades = eligibleTrades.filter(
    (t) =>
      unmatchedTradeIds.includes(t.id) &&
      !matchedPairs.some((p) => p.trade_id === t.id)
  );

  const handleManualMatch = useCallback((trade: EligibleTrade) => {
    if (!currentUnmatchedFile) return;

    const newPair: MatchedPair = {
      filename: currentUnmatchedFile.filename,
      temp_path: currentUnmatchedFile.temp_path,
      trade_id: trade.id,
      trade_number: trade.TradeNumber,
      btn: trade.bankTradeNumber || '',
      entity_id: trade.entityId,
      signed_url: currentUnmatchedFile.signed_url,
      matchType: 'manual',
    };
    setMatchedPairs((prev) => [...prev, newPair]);

    // In single-assign mode, return to review after matching
    if (singleAssignTradeId) {
      setSingleAssignTradeId(null);
      setStep('review');
      return;
    }

    // Move to next unmatched file or go to review
    if (manualMatchIndex + 1 < unmatchedFiles.length) {
      setManualMatchIndex((i) => i + 1);
      setPdfPageNumber(1);
    } else {
      setStep('review');
    }
  }, [currentUnmatchedFile, manualMatchIndex, unmatchedFiles.length, singleAssignTradeId]);

  const handleSkipFile = useCallback(() => {
    if (!currentUnmatchedFile) return;
    setSkippedFiles((prev) => [...prev, currentUnmatchedFile]);

    if (manualMatchIndex + 1 < unmatchedFiles.length) {
      setManualMatchIndex((i) => i + 1);
      setPdfPageNumber(1);
    } else {
      // All files exhausted — return to review
      if (singleAssignTradeId) setSingleAssignTradeId(null);
      setStep('review');
    }
  }, [currentUnmatchedFile, manualMatchIndex, unmatchedFiles.length, singleAssignTradeId]);

  // ─── Step 5: Process pairs (one at a time for real progress) ───
  const handleProcess = useCallback(async (analyse: boolean) => {
    const pairs = matchedPairs.map((p) => ({
      trade_id: p.trade_id,
      temp_pdf_path: p.temp_path,
      filename: p.filename,
      entity_id: p.entity_id,
    }));

    if (pairs.length === 0) return;

    setStep('processing');
    setProcessProgress({ current: 1, total: pairs.length });

    const allResults: ProcessResult[] = [];

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];
      // Only pass session_id on the last pair so cleanup runs once
      const sid = i === pairs.length - 1 ? sessionId : '';

      try {
        const result = await clientService.massProcessContracts(
          clientId,
          [pair],
          analyse,
          sid,
        );

        // Enrich with trade number from our local data
        for (const r of (result.results || [])) {
          const mp = matchedPairs.find((p) => p.trade_id === r.trade_id);
          allResults.push({
            ...r,
            trade_number: r.trade_number || mp?.trade_number,
          });
        }
      } catch (e: any) {
        allResults.push({
          trade_id: pair.trade_id,
          trade_number: matchedPairs.find((p) => p.trade_id === pair.trade_id)?.trade_number,
          status: 'error',
          error: e.message || 'Processing failed',
        });
      }

      setProcessProgress({ current: i + 1, total: pairs.length });
    }

    setProcessResults(allResults);
    setStep('results');
  }, [clientId, matchedPairs, sessionId]);

  // ─── Reset on close ───
  const handleClose = useCallback(() => {
    setStep('scope');
    setSelectedCounterpartyId('');
    setSelectedValueDate('');
    setEligibleTrades([]);
    setSelectedTradeIds(new Set());
    setUploadedFiles([]);
    setSessionId('');
    setMatchedPairs([]);
    setUnmatchedFiles([]);
    setUnmatchedTradeIds([]);
    setManualMatchIndex(0);
    setSkippedFiles([]);
    setSingleAssignTradeId(null);
    setProcessResults([]);
    setError('');
    setHasSearched(false);
    onClose();
  }, [onClose]);

  const handleFinish = useCallback(() => {
    onComplete();
    handleClose();
  }, [onComplete, handleClose]);

  if (!isOpen) return null;

  // ─── Helpers ───
  const formatNumber = (n: number) =>
    n != null ? n.toLocaleString('es-CL', { maximumFractionDigits: 2 }) : '—';

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    // Convert YYYY-MM-DD to DD/MM/YYYY
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  // ─── Render ───
  return (
    <div className="mass-upload-backdrop">
      <div className="mass-upload-modal">
        {/* Header */}
        <div className="mass-upload-header">
          <h3>{t('massUpload.title')}</h3>
          <button className="mass-upload-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mass-upload-error">
            <XCircle size={14} />
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Content */}
        <div className="mass-upload-content">

          {/* ══════════ Step 1: Scope ══════════ */}
          {step === 'scope' && (
            <div className="mass-upload-scope">
              <div className="mass-upload-filters">
                <div className="filter-group">
                  <label>{t('massUpload.counterparty')}</label>
                  <select
                    value={selectedCounterpartyId}
                    onChange={(e) => setSelectedCounterpartyId(e.target.value)}
                  >
                    <option value="">{t('massUpload.selectCounterparty')}</option>
                    {banks.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>{t('massUpload.valueDate')}</label>
                  <input
                    type="date"
                    value={selectedValueDate}
                    onChange={(e) => setSelectedValueDate(e.target.value)}
                  />
                </div>
                <div className="filter-group filter-group-btn">
                  <button
                    className="btn-primary btn-small"
                    disabled={!selectedCounterpartyId || !selectedValueDate || loadingTrades}
                    onClick={fetchEligibleTrades}
                  >
                    {t('massUpload.search')}
                  </button>
                </div>
              </div>

              {loadingTrades && (
                <div className="mass-upload-loading">
                  <Loader2 size={18} className="spin" />
                  <span>{t('massUpload.loadingTrades')}</span>
                </div>
              )}

              {!loadingTrades && hasSearched && (
                <>
                  <div className="mass-upload-trade-count">
                    {eligibleTrades.length === 0
                      ? t('massUpload.noEligibleTrades')
                      : t('massUpload.eligibleTradeCount', { count: eligibleTrades.length })}
                  </div>

                  {eligibleTrades.length > 0 && (
                    <div className="mass-upload-trade-list">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: 36 }}>
                              <input
                                type="checkbox"
                                checked={selectedTradeIds.size === eligibleTrades.length}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedTradeIds(new Set(eligibleTrades.map((t) => t.id)));
                                  } else {
                                    setSelectedTradeIds(new Set());
                                  }
                                }}
                              />
                            </th>
                            <th>{t('massUpload.tradeNumber')}</th>
                            <th>{t('massUpload.counterpartyTradeNo')}</th>
                            <th>{t('massUpload.ourParty')}</th>
                            <th>{t('massUpload.direction')}</th>
                            <th className="num">{t('massUpload.amount')}</th>
                            <th>{t('massUpload.currencyPair')}</th>
                            <th className="num">{t('massUpload.price')}</th>
                            <th>{t('massUpload.maturityDate')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eligibleTrades.map((trade) => (
                            <tr key={trade.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedTradeIds.has(trade.id)}
                                  onChange={(e) => {
                                    const next = new Set(selectedTradeIds);
                                    if (e.target.checked) next.add(trade.id);
                                    else next.delete(trade.id);
                                    setSelectedTradeIds(next);
                                  }}
                                />
                              </td>
                              <td>{trade.TradeNumber}</td>
                              <td>{trade.bankTradeNumber || '—'}</td>
                              <td>{trade.OurParty || '—'}</td>
                              <td>{trade.Direction}</td>
                              <td className="num">{formatNumber(trade.QuantityCurrency1)}</td>
                              <td>{trade.Currency1}/{trade.Currency2}</td>
                              <td className="num">{formatNumber(trade.Price)}</td>
                              <td>{formatDate(trade.MaturityDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══════════ Step 2: Upload ══════════ */}
          {step === 'upload' && (
            <div className="mass-upload-files">
              <div className="mass-upload-dropzone">
                <Upload size={32} />
                <p>{t('massUpload.selectFiles')}</p>
                <p className="mass-upload-hint">
                  {t('massUpload.maxFiles', { count: selectedTradeIds.size })}
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  disabled={uploading}
                />
              </div>
              {uploading && (
                <div className="mass-upload-loading">
                  <Loader2 size={18} className="spin" />
                  <span>{t('massUpload.uploading')}</span>
                </div>
              )}
            </div>
          )}

          {/* ══════════ Step 3: Manual Matching ══════════ */}
          {step === 'matching' && currentUnmatchedFile && (
            <div className="mass-upload-manual">
              <div className="mass-upload-manual-header">
                <span>
                  {t('massUpload.manualMatchProgress', {
                    current: manualMatchIndex + 1,
                    total: unmatchedFiles.length,
                  })}
                </span>
                <span className="mass-upload-filename">
                  <FileText size={14} /> {currentUnmatchedFile.filename}
                </span>
              </div>

              <div className="mass-upload-manual-content">
                {/* Left: PDF preview */}
                <div className="mass-upload-pdf-preview" ref={pdfContainerRef}>
                  {currentUnmatchedFile.signed_url && (
                    <>
                      <div className="mass-upload-pdf-nav">
                        <button
                          disabled={pdfPageNumber <= 1}
                          onClick={() => setPdfPageNumber((p) => p - 1)}
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <span>{pdfPageNumber} / {pdfNumPages}</span>
                        <button
                          disabled={pdfPageNumber >= pdfNumPages}
                          onClick={() => setPdfPageNumber((p) => p + 1)}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                      <Document
                        file={currentUnmatchedFile.signed_url}
                        onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                        loading={
                          <div className="mass-upload-loading">
                            <Loader2 size={18} className="spin" />
                          </div>
                        }
                      >
                        <Page
                          pageNumber={pdfPageNumber}
                          width={pdfWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </>
                  )}
                </div>

                {/* Right: Trade list */}
                <div className="mass-upload-trade-picker">
                  <h4>{t('massUpload.selectTrade')}</h4>
                  {(() => {
                    const tradesToShow = singleAssignTradeId
                      ? eligibleTrades.filter((t) => t.id === singleAssignTradeId)
                      : remainingUnmatchedTrades;
                    return tradesToShow.length === 0 ? (
                      <p className="mass-upload-no-trades">{t('massUpload.noRemainingTrades')}</p>
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>{t('massUpload.tradeNumber')}</th>
                            <th>{t('massUpload.counterpartyTradeNo')}</th>
                            <th>{t('massUpload.ourParty')}</th>
                            <th className="num">{t('massUpload.amount')}</th>
                            <th>{t('massUpload.currencyPair')}</th>
                            <th className="num">{t('massUpload.price')}</th>
                            <th>{t('massUpload.maturityDate')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tradesToShow.map((trade) => (
                            <tr
                              key={trade.id}
                              className="mass-upload-trade-row-clickable"
                              onClick={() => handleManualMatch(trade)}
                            >
                              <td>{trade.TradeNumber}</td>
                              <td>{trade.bankTradeNumber || '—'}</td>
                              <td>{trade.OurParty || '—'}</td>
                              <td className="num">{formatNumber(trade.QuantityCurrency1)}</td>
                              <td>{trade.Currency1}/{trade.Currency2}</td>
                              <td className="num">{formatNumber(trade.Price)}</td>
                              <td>{formatDate(trade.MaturityDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* ══════════ Step 4: Review ══════════ */}
          {step === 'review' && (() => {
            // Compute files available for assignment (not matched, not skipped)
            const matchedPaths = new Set(matchedPairs.map((p) => p.temp_path));
            const skippedPaths = new Set(skippedFiles.map((f) => f.temp_path));
            const allFiles = [...uploadedFiles, ...unmatchedFiles];
            const availableFiles = allFiles.filter(
              (f, i, arr) =>
                !matchedPaths.has(f.temp_path) &&
                !skippedPaths.has(f.temp_path) &&
                arr.findIndex((x) => x.temp_path === f.temp_path) === i // dedupe
            );
            return (
            <div className="mass-upload-review">
              <h4>{t('massUpload.reviewTitle')}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t('massUpload.tradeNumber')}</th>
                    <th>{t('massUpload.counterpartyTradeNo')}</th>
                    <th>{t('massUpload.valueDate')}</th>
                    <th className="num">{t('massUpload.amount')}</th>
                    <th>{t('massUpload.currencyPair')}</th>
                    <th>{t('massUpload.contract')}</th>
                    <th>{t('massUpload.matchType')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleTrades
                    .filter((t) => selectedTradeIds.has(t.id))
                    .map((trade) => {
                      const pair = matchedPairs.find((p) => p.trade_id === trade.id);
                      return (
                        <tr key={trade.id}>
                          <td>{trade.TradeNumber}</td>
                          <td>{trade.bankTradeNumber || '—'}</td>
                          <td>{formatDate(trade.ValueDate)}</td>
                          <td className="num">{formatNumber(trade.QuantityCurrency1)}</td>
                          <td>{trade.Currency1}/{trade.Currency2}</td>
                          <td>{pair ? pair.filename : '—'}</td>
                          <td>
                            {pair ? (
                              <span className={`match-badge match-${pair.matchType}`}>
                                {pair.matchType === 'auto' ? 'Auto' : 'Manual'}
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {pair ? (
                              <button
                                className="mass-upload-action-btn unassign"
                                title={t('massUpload.unassign')}
                                onClick={() => {
                                  setMatchedPairs((prev) => prev.filter((p) => p.trade_id !== trade.id));
                                }}
                              >
                                <Undo2 size={13} />
                              </button>
                            ) : availableFiles.length > 0 && (
                              <button
                                className="mass-upload-action-btn assign"
                                title={t('massUpload.assignFile')}
                                onClick={() => {
                                  setSingleAssignTradeId(trade.id);
                                  setUnmatchedFiles(availableFiles);
                                  setManualMatchIndex(0);
                                  setPdfPageNumber(1);
                                  setStep('matching');
                                }}
                              >
                                <Paperclip size={13} />
                              </button>
                            )}
                            <button
                              className="mass-upload-action-btn remove"
                              title={t('massUpload.removeTrade')}
                              onClick={() => {
                                if (pair) {
                                  setMatchedPairs((prev) => prev.filter((p) => p.trade_id !== trade.id));
                                }
                                setSelectedTradeIds((prev) => {
                                  const next = new Set(prev);
                                  next.delete(trade.id);
                                  return next;
                                });
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {skippedFiles.length > 0 && (
                <div className="mass-upload-skipped">
                  <h5>{t('massUpload.skippedFiles')}</h5>
                  <ul>
                    {skippedFiles.map((f, i) => (
                      <li key={i}><FileText size={12} /> {f.filename}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            );
          })()}

          {/* ══════════ Step 5: Processing overlay ══════════ */}
          {step === 'processing' && (
            <div className="mass-upload-processing">
              <div className="section-spinner" />
              <p>{t('massUpload.processing', {
                current: processProgress.current,
                total: processProgress.total,
              })}</p>
              <p className="mass-upload-processing-hint">{t('massUpload.processingHint')}</p>
            </div>
          )}

          {/* ══════════ Step 6: Results ══════════ */}
          {step === 'results' && (
            <div className="mass-upload-results">
              <h4>{t('massUpload.resultsTitle')}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t('massUpload.tradeNumber')}</th>
                    <th>{t('massUpload.counterpartyTradeNo')}</th>
                    <th className="num">{t('massUpload.amount')}</th>
                    <th>{t('massUpload.contract')}</th>
                    <th>{t('massUpload.analysis')}</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleTrades
                    .filter((t) => selectedTradeIds.has(t.id))
                    .map((trade) => {
                      const pair = matchedPairs.find((p) => p.trade_id === trade.id);
                      const result = processResults.find((r) => r.trade_id === trade.id);
                      return (
                        <tr key={trade.id}>
                          <td>{trade.TradeNumber}</td>
                          <td>{trade.bankTradeNumber || '—'}</td>
                          <td className="num">{formatNumber(trade.QuantityCurrency1)}</td>
                          <td>{pair ? pair.filename : '—'}</td>
                          <td>
                            {result ? (
                              result.status === 'error' ? (
                                <span className="result-badge result-error">
                                  <XCircle size={12} /> {t('massUpload.resultError')}
                                </span>
                              ) : result.contract_status === 'pending' ? (
                                <span className="result-badge result-pending">{t('massUpload.resultPending')}</span>
                              ) : result.contract_status === 'Contract OK' ? (
                                <span className="result-badge result-ok">
                                  <CheckCircle size={12} /> {t('massUpload.resultContractOk')}
                                </span>
                              ) : result.contract_status === 'Needs Review' ? (
                                <span className="result-badge result-diff">
                                  {t('massUpload.resultNeedsReview')}
                                </span>
                              ) : (
                                <span className="result-badge result-diff">
                                  {t('massUpload.resultDifference')}
                                </span>
                              )
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              {skippedFiles.length > 0 && (
                <div className="mass-upload-skipped">
                  <h5>{t('massUpload.skippedFiles')}</h5>
                  <ul>
                    {skippedFiles.map((f, i) => (
                      <li key={i}><FileText size={12} /> {f.filename}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mass-upload-footer">
          {step === 'scope' && (
            <button
              className="btn-primary"
              disabled={selectedTradeIds.size === 0}
              onClick={() => setStep('upload')}
            >
              {t('massUpload.next')}
            </button>
          )}

          {step === 'upload' && (
            <button
              className="btn-secondary"
              onClick={() => setStep('scope')}
            >
              {t('massUpload.back')}
            </button>
          )}

          {step === 'matching' && (
            <>
              {singleAssignTradeId && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setSingleAssignTradeId(null);
                    setStep('review');
                  }}
                >
                  {t('massUpload.back')}
                </button>
              )}
              <button
                className="btn-secondary"
                onClick={handleSkipFile}
              >
                {t('massUpload.skip')}
              </button>
            </>
          )}

          {step === 'review' && (
            <>
              <button
                className="btn-secondary"
                disabled={matchedPairs.length === 0}
                onClick={() => handleProcess(false)}
              >
                {t('massUpload.uploadOnly')}
              </button>
              <button
                className="btn-primary"
                disabled={matchedPairs.length === 0}
                onClick={() => handleProcess(true)}
              >
                {t('massUpload.uploadAndAnalyse')}
              </button>
            </>
          )}

          {step === 'results' && (
            <button className="btn-primary" onClick={handleFinish}>
              {t('massUpload.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MassContractUploadModal;
